import { request, TokenGetter } from './learningApi';

/** `material_metadata.preprocess`, written by the Knowledge Hub processing job. */
export interface PreprocessState {
  requested?: boolean;
  notes?: string;
  status?: 'queued' | 'running' | 'done' | 'failed' | null;
  method?: 'ai' | 'structured';
  ai?: 'done' | 'partial' | 'skipped' | 'failed' | null;
  reason?: string | null;
  progress?: { done: number; total: number } | null;
  units?: number | null;
  chunks?: number | null;
  parts?: number | null;
  fallback_parts?: number | null;
  pages?: number | null;
  ocr_pages?: number | null;
  chars?: number | null;
  credits?: number | null;
  /** Credits the run was expected to take: usually, and if every part needed a second call. */
  estimate?: number | null;
  estimate_max?: number | null;
  calls?: number | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  finished_at?: string | null;
}

export interface PreprocessConfig {
  enabled: boolean;
  default_on_extensions: string[];
  default_off_extensions: string[];
  default_on_max_bytes: number;
  max_chars_per_file: number;
  max_credits_per_file: number;
  credits_per_100k_chars: number;
  max_credits_per_100k_chars: number;
  max_notes_chars: number;
}

export interface RawTextPage {
  total: number;
  kind: string;
  header: string | null;
  chars: number;
  ocr_pages: number;
  items: { label: string | null; page: number | null; text: string }[];
}

export interface CategoryUsage {
  id: string;
  name: string;
  materials: number;
  chains: { id: string; name: string }[];
  managed: boolean;
}

const base = '/api/v2/customers/current';

export async function fetchPreprocessConfig(getToken: TokenGetter): Promise<PreprocessConfig> {
  return (await request<{ data: PreprocessConfig }>(getToken, '/api/v2/materials/preprocess-config')).data;
}

export async function setMaterialPreprocess(
  getToken: TokenGetter, materialId: string, enable: boolean, notes?: string,
): Promise<{ queued: boolean }> {
  const body: Record<string, unknown> = { enable };
  if (notes !== undefined) body.notes = notes;
  return (await request<{ data: { queued: boolean } }>(getToken, `${base}/materials/${materialId}/preprocess`, {
    method: 'POST',
    body: JSON.stringify(body),
  })).data;
}

export async function fetchMaterialRawText(
  getToken: TokenGetter, materialId: string, opts: { limit?: number; offset?: number } = {},
): Promise<RawTextPage> {
  const params = new URLSearchParams({ limit: String(opts.limit ?? 50), offset: String(opts.offset ?? 0) });
  return (await request<{ data: RawTextPage }>(getToken, `${base}/materials/${materialId}/raw-text?${params}`)).data;
}

export async function fetchCategoryUsage(getToken: TokenGetter, categoryId: string): Promise<CategoryUsage> {
  return (await request<{ data: CategoryUsage }>(getToken, `${base}/categories/${categoryId}/usage`)).data;
}

export async function deleteCategory(
  getToken: TokenGetter, categoryId: string, deleteMaterials: boolean,
): Promise<{ materials_deleted: number; materials_uncategorized: number }> {
  return (await request<{ data: { materials_deleted: number; materials_uncategorized: number } }>(
    getToken, `${base}/categories/${categoryId}?delete_materials=${deleteMaterials}`, { method: 'DELETE' },
  )).data;
}

const extOf = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

/** Same rule as the server's `default_on`: documents up to the size limit, not data files. */
export const preprocessDefaultOn = (file: File, cfg: PreprocessConfig | null) =>
  !!cfg?.enabled && cfg.default_on_extensions.includes(extOf(file.name)) && file.size <= cfg.default_on_max_bytes;

/** Rough characters of text in a file, for the credit estimate (PDF/Office files are mostly not text). */
export const estimateTextChars = (file: File) => {
  const ext = extOf(file.name);
  if (['.txt', '.md', '.markdown', '.csv', '.tsv', '.json', '.jsonl', '.ndjson'].includes(ext)) return file.size;
  if (['.html', '.htm'].includes(ext)) return file.size * 0.4;
  if (['.docx', '.pptx', '.xlsx'].includes(ext)) return file.size * 0.25;
  return file.size * 0.05;
};

/** [usual, most] credits for `chars` of text; "most" is every part needing a second call. */
export const creditRange = (chars: number, cfg: PreprocessConfig): [number, number] => [
  Math.min(cfg.max_credits_per_file, (chars / 100_000) * cfg.credits_per_100k_chars),
  Math.min(cfg.max_credits_per_file, (chars / 100_000) * (cfg.max_credits_per_100k_chars ?? cfg.credits_per_100k_chars)),
];

export const estimateCredits = (file: File, cfg: PreprocessConfig) => creditRange(estimateTextChars(file), cfg)[0];

export const fmtCredits = (c: number) => (c < 1 ? c.toFixed(2) : c < 10 ? c.toFixed(1) : Math.round(c).toLocaleString());
